/**
 * Emisor de telemetría de la demo compartida (blueprint §B.6).
 *
 * Manda un beacon por cada acción instrumentada, con el `leadId` que salió de
 * canjear el enlace mágico. Ese identificador es el correlacionador: no la
 * dirección de red, que cambia, ni la billetera, que en simulacro puede no
 * existir.
 *
 * TRES REGLAS QUE GOBIERNAN ESTE ARCHIVO
 *
 * 1. La telemetría JAMÁS estorba. Si el servidor no responde, si no hay red, si
 *    el prospecto entró sin enlace — la demo sigue funcionando exactamente
 *    igual. Ninguna llamada de aquí puede lanzar hacia arriba.
 *
 * 2. El peso lo decide el SERVIDOR. Aquí solo se dice qué pasó. Un cliente que
 *    pudiera fijar cuánto vale su propia acción no sería telemetría, sería un
 *    formulario de autopuntuación.
 *
 * 3. La deduplicación real vive en la base, con un índice único. Lo de aquí es
 *    solo una caché en memoria para no gastar red en lo que ya se sabe repetido
 *    dentro de la misma sesión.
 */

import { leadIdActual } from './lead-ref'

/**
 * El catálogo cerrado de eventos. El servidor rechaza cualquier otro con un 400.
 *
 * Dos familias. La del INVERSOR mide a alguien evaluando invertir; la del EMISOR
 * mide a alguien ensayando el trabajo de dueño de la plataforma. La segunda pesa
 * mucho más, y no es una cuestión de grado: quien pide una demostración de esto
 * casi nunca quiere invertir, quiere vender la plataforma.
 *
 * Los pesos viven SOLO en el servidor (`apps/api/src/demo/scoring.ts`). Aquí no
 * hay ni uno a propósito: un cliente que pudiera fijar cuánto vale su propia
 * acción no sería telemetría, sería un formulario de autopuntuación.
 */
export type EventoDemo =
  // Familia del inversor
  | 'demo_opened'
  | 'project_viewed'
  | 'calculator_used'
  | 'purchase_simulated'
  | 'dividend_claimed'
  | 'return_visit'
  // El cruce: dejó de evaluar la inversión y empezó a evaluar el producto
  | 'issuer_panel_opened'
  // Familia del emisor
  | 'issuer_project_created'
  | 'issuer_dividend_distributed'
  | 'issuer_sale_configured'
  | 'issuer_investor_reviewed'
  | 'issuer_treasury_viewed'
  | 'issuer_api_key_created'
  // Declaración explícita
  | 'checkout_intent'

const API = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/**
 * Lo ya enviado en esta pestaña. Evita gastar red en repeticiones obvias —abrir
 * la misma ficha tres veces seguidas—, pero NO es la fuente de verdad: si el
 * prospecto recarga, se vacía, y el índice único de la base vuelve a filtrar.
 */
const enviados = new Set<string>()

/**
 * Misma regla que el servidor, solo para la caché local.
 *
 * Es un ESPEJO de `claveDeduplicacion` en `apps/api/src/demo/scoring.ts`, y si
 * las dos divergen no pasa nada grave: la de aquí solo evita gastar red, y el
 * índice único de la base sigue siendo quien decide de verdad. Por eso se puede
 * mantener a mano sin miedo — lo peor que provoca un descuido es un beacon de
 * más que el servidor rechaza en silencio.
 */
function claveLocal(tipo: EventoDemo, meta?: Record<string, unknown>): string {
  const dia = new Date().toISOString().slice(0, 10)
  const proyecto = typeof meta?.projectId === 'string' ? meta.projectId : 'sin-proyecto'

  // Una sola vez en toda la vida del prospecto.
  if (tipo === 'demo_opened' || tipo === 'checkout_intent' || tipo === 'issuer_api_key_created') {
    return tipo
  }

  // Una vez por activo y día: publicar dos activos distintos dice más que
  // republicar el mismo diez veces mientras se juega con el formulario.
  if (
    tipo === 'project_viewed' ||
    tipo === 'issuer_project_created' ||
    tipo === 'issuer_dividend_distributed'
  ) {
    return `${tipo}:${proyecto}:${dia}`
  }

  return `${tipo}:${dia}`
}

/**
 * Registra una acción del prospecto.
 *
 * No devuelve promesa a propósito: quien la llama está en medio de un manejador
 * de clic y no debe esperar ni encadenar nada. Si hace falta saber el resultado
 * —solo en pruebas— está `emitirYEsperar`.
 */
export function registrar(tipo: EventoDemo, meta?: Record<string, unknown>): void {
  void emitirYEsperar(tipo, meta)
}

/** Variante esperable. Solo para pruebas: en la interfaz se usa `registrar`. */
export async function emitirYEsperar(
  tipo: EventoDemo,
  meta?: Record<string, unknown>,
): Promise<boolean> {
  const leadId = leadIdActual()
  // Sin prospecto no hay a quién atribuir: alguien que abrió la demo sin enlace.
  // Es un caso normal, no un error.
  if (!leadId) return false

  const clave = claveLocal(tipo, meta)
  if (enviados.has(clave)) return false
  enviados.add(clave)

  const cuerpo = JSON.stringify({ leadId, type: tipo, meta: meta ?? null })

  try {
    // `sendBeacon` es lo correcto aquí: sobrevive a que la pestaña se cierre o
    // navegue, que es justo cuando se pierden los eventos interesantes —el
    // prospecto que pulsa "activa tu plataforma" y se va.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(
        `${API}/demo/telemetry`,
        new Blob([cuerpo], { type: 'application/json' }),
      )
      if (ok) return true
      // Si el navegador lo rechaza (cola llena), se cae al fetch de abajo.
    }

    await fetch(`${API}/demo/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: cuerpo,
      keepalive: true,
    })
    return true
  } catch {
    // Se saca de la caché para que un fallo de red no pierda el evento para
    // siempre: el próximo intento dentro de la misma sesión vuelve a probar.
    enviados.delete(clave)
    return false
  }
}

/** Solo para pruebas: vacía la caché de la sesión. */
export function _limpiarCache(): void {
  enviados.clear()
}
